import {
  Form,
  Input,
  Modal,
  Spin,
  Typography,
  Select,
  Button,
  Popconfirm,
  Tooltip,
  Col,
  Row,
  Space,
  Checkbox,
} from 'antd';
import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CloseOutlined, UndoOutlined } from '@ant-design/icons';
import componentContent from './UserEditModal.content.json';
import useTranslate from '@hooks/useTranslate';
import { useStore } from 'effector-react';
import moment from 'moment';
import { useForm } from 'antd/lib/form/Form';
import modals, { closeModal, IModalsState } from '@stores/modals';
import IUser from '@interfaces/IUser';
import ICategory from '@interfaces/ICategory';
import users, {
  updateUser,
  createUser,
  verifyUser,
  recoverPassword,
  unblockUser,
  blockUser,
  unlinkDiscord,
} from '@stores/users';
import * as roles from '@stores/roles';
import BlockingUserButton from '@components/BlockingUserButton';
import useGuards from '@hooks/useGuards';
import type { UserBlockReason } from '@constants/userBlockOptions';
import locale from '@stores/locale';
import { getCountryOptions } from '@helpers/countries';

const FormItem = Form.Item;
const { Option } = Select;

const filterValues = (values: Record<string, any>, data?: Record<string, any>) => {
  if (data) {
    const filtered = Object.entries(values).filter(([key, value]) => {
      return value !== data[key];
    });
    return Object.fromEntries(filtered);
  }
  return values;
};

const mapDataToFieldsValue = (data?: IUser, list?: ICategory[]) => {
  return {
    email: data?.email,
    enabled: data?.enable,
    link: data?.link,
    name: data?.name,
    nickname: data?.nickname,
    password: Math.random().toString(36).substring(0, 7),
    phone: data?.contactPhone,
    roleId: list?.find((role: ICategory) => {
      return role.name === data?.role;
    })?.id,
    contactPhone: data?.contactPhone,
    about: data?.about,
    phoneAuth: data?.phone,
    isOfficial: data?.isOfficial,
    countryCode: data?.countryCode,
  };
};

const UserEditModal: FunctionComponent<unknown> = () => {
  const { isAdmin, isModerator } = useGuards();
  const content = useTranslate(componentContent);
  const localeName = useStore(locale);
  const countryOptions = useMemo(() => getCountryOptions(localeName), [localeName]);
  const {
    visibleState: { user: visible },
    data,
  } = useStore<IModalsState<IUser>>(modals);
  const { preloaders } = useStore(users);
  const { list } = useStore(roles.default);
  const [form] = useForm();

  const [recoverMessageVisible, setRecoverMessageVisible] = useState(false);
  const [isDisableSubmit, setIsDisableSubmit] = useState(false);
  const [authTypeChangeConfirmVisible, setAuthTypeChangeConfirmVisible] = useState(false);
  const [unlinkRequested, setUnlinkRequested] = useState(false);
  const [unlinkComment, setUnlinkComment] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const initialValues = useRef<Record<string, unknown>>({});
  const discordUsername = data?.linkedServices?.find(
    (service) => service.provider === 'DISCORD',
  )?.username;
  const canUnlinkDiscord = isAdmin || isModerator;
  const isProcessing = isSaving || !!preloaders.recoverPassword;
  const isSaveDisabled =
    isProcessing ||
    (isAdmin && isDisableSubmit) ||
    (unlinkRequested && !unlinkComment.trim()) ||
    (!isAdmin && !(isModerator && unlinkRequested));

  useEffect(() => {
    if (!isAdmin) return;
    roles.getList();
    return () => {
      roles.reset();
    };
  }, [isAdmin]);

  useEffect(() => {
    setUnlinkRequested(false);
    setUnlinkComment('');
    form.resetFields();
    if (visible && data) {
      const fieldsValue = mapDataToFieldsValue(data, list);
      initialValues.current = fieldsValue;
      form.setFieldsValue(fieldsValue);
      setIsDisableSubmit(false);
      setAuthTypeChangeConfirmVisible(false);
    } else {
      form.setFieldsValue(mapDataToFieldsValue());
    }
  }, [visible, data, form, list]);

  const onFinish = async (values: Partial<IUser> & { discordUnlinkComment?: string }) => {
    if (savingRef.current || (!isAdmin && !isModerator)) return;
    const { discordUnlinkComment, countryCode, ...profileValues } = values;
    const comment = discordUnlinkComment?.trim() || '';
    if (unlinkRequested && !comment) {
      form.setFields([{ name: 'discordUnlinkComment', errors: [content.ticketRequired] }]);
      return;
    }

    savingRef.current = true;
    setIsSaving(true);
    setAuthTypeChangeConfirmVisible(false);
    try {
      if (isAdmin) {
        if (data?.id) {
          const { isOfficial, ...filteredData } = filterValues(
            profileValues,
            initialValues.current,
          );
          if (countryCode && countryCode !== initialValues.current.countryCode) {
            filteredData.countryCode = countryCode;
            filteredData.ignoreDeviceUpdate = true;
          }
          if (Object.keys(filteredData).length) {
            await updateUser({ userId: data.id, data: filteredData, preloaderId: data.id });
            initialValues.current = { ...initialValues.current, ...filteredData };
          }
          if (
            Boolean(isOfficial) !== Boolean(initialValues.current.isOfficial) &&
            isOfficial !== undefined
          ) {
            await verifyUser({ userId: data.id, data: { isOfficial }, preloaderId: data.id });
            initialValues.current.isOfficial = isOfficial;
          }
        } else {
          const response = await createUser(profileValues);
          if (profileValues.isOfficial) {
            const userId = response.data.body?.id ?? response.data.id;
            await verifyUser({ userId, data: { isOfficial: true }, preloaderId: userId });
          }
        }
      }

      if (data?.id && unlinkRequested && canUnlinkDiscord) {
        await unlinkDiscord({ userId: data.id, comment });
      }
      closeModal({ name: 'user' });
    } catch {
      return;
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const isDisableEmail = useMemo(() => {
    return data && (data.authType === 'GOOGLE' || data.authType === 'APPLE');
  }, [data]);

  const onPhoneOrEmailChangeHandler = useCallback(() => {
    setIsDisableSubmit(!(form.getFieldValue('email') || form.getFieldValue('phone')));
  }, [form]);

  const checkBeforeSubmit = useCallback(async () => {
    if (isSaveDisabled || savingRef.current) return;
    try {
      await form.validateFields();
    } catch {
      return;
    }
    if (
      isAdmin &&
      data &&
      (form.getFieldValue('email') !== data?.email ||
        form.getFieldValue('phoneAuth') !== data?.phone)
    ) {
      setAuthTypeChangeConfirmVisible(true);
    } else {
      form.submit();
    }
  }, [data, form, isAdmin, isSaveDisabled]);

  const recoverPasswordHandler = useCallback(async () => {
    const response = await recoverPassword({
      email: data?.email,
    });
    if (response.status === 200 && response.data.body.stage === 'NEW_PASS') {
      setRecoverMessageVisible(true);
    }
  }, [data]);

  const onBlock = useCallback(
    (user: IUser, daysToUnblock?: number, daysToClean?: number, reason?: UserBlockReason) => {
      blockUser({
        userId: user.id,
        preloaderId: user.id,
        user,
        daysToUnblock,
        daysToClean,
        reason,
      });
    },
    [data],
  );

  const onUnblock = useCallback((user: IUser) => {
    unblockUser({
      userId: user.id,
      preloaderId: user.id,
      user,
    });
  }, []);

  const renderBlockContainer = () => {
    const isBlocking = preloaders.blockUser === data.id;
    const isUnblocking = preloaders.unblockUser === data.id;

    return (
      <BlockingUserButton
        unblock={onUnblock}
        block={onBlock}
        processing={isBlocking || isUnblocking}
        user={data}
        content={content}
      />
    );
  };

  if (!visible) return null;
  return (
    <Form
      onFinish={onFinish}
      onValuesChange={(_, values) => setUnlinkComment(values.discordUnlinkComment || '')}
      form={form}
      disabled={isSaving}
      labelAlign='left'
      labelWrap
      labelCol={{ flex: '180px' }}
    >
      <Modal
        title={content.title}
        confirmLoading={isProcessing}
        visible
        closable={!isProcessing}
        maskClosable={!isProcessing}
        keyboard={!isProcessing}
        onCancel={() => !savingRef.current && !isProcessing && closeModal({ name: 'user' })}
        width={800}
        footer={[
          <Button
            key='cancel'
            disabled={isProcessing}
            onClick={() => closeModal({ name: 'user' })}
          >
            {content.cancel}
          </Button>,
          <Tooltip
            key='submit'
            title={isDisableSubmit ? content.authTypeWarning : ''}
          >
            <Button
              type='primary'
              onClick={checkBeforeSubmit}
              disabled={isSaveDisabled}
              loading={isSaving}
            >
              {content.save}
            </Button>
          </Tooltip>,
          <Popconfirm
            key='confirmSubmit'
            visible={authTypeChangeConfirmVisible}
            title={content.authTypeChangeConfirm}
            showCancel={true}
            onCancel={() => setAuthTypeChangeConfirmVisible(false)}
            onConfirm={form.submit}
            okButtonProps={{ disabled: isSaveDisabled, loading: isSaving }}
          ></Popconfirm>,
        ]}
      >
        <Spin spinning={isProcessing}>
          {data ? (
            <Row>
              <Col span={12}>
                <Space size={20}>
                  <FormItem
                    label='id'
                    labelCol={{ flex: '10px' }}
                  >
                    <Typography.Text type='secondary'>{data.id}</Typography.Text>
                  </FormItem>
                  {isAdmin && <FormItem name='enabled'>{renderBlockContainer()}</FormItem>}
                </Space>
              </Col>
              {!!data.deactivatedUntil && (
                <Col span={12}>
                  <FormItem
                    label={content.deactivatedUntil}
                    labelCol={{ flex: '150px' }}
                    name='deactivatedUntil'
                  >
                    <Typography.Text type='secondary'>
                      {moment(data.deactivatedUntil).format('DD.MM.YYYY')}
                    </Typography.Text>
                  </FormItem>
                </Col>
              )}
            </Row>
          ) : null}
          <FormItem
            label={content.nickname}
            name='nickname'
          >
            <Input readOnly={!isAdmin} />
          </FormItem>
          {data?.id && (
            <FormItem label='Discord'>
              <Input
                readOnly
                value={unlinkRequested ? '' : discordUsername || '—'}
                placeholder='—'
                suffix={
                  discordUsername && canUnlinkDiscord ? (
                    <Tooltip
                      title={unlinkRequested ? content.cancelUnlinkDiscord : content.unlinkDiscord}
                    >
                      <Button
                        type='text'
                        size='small'
                        disabled={isProcessing}
                        icon={unlinkRequested ? <UndoOutlined /> : <CloseOutlined />}
                        aria-label={
                          unlinkRequested ? content.cancelUnlinkDiscord : content.unlinkDiscord
                        }
                        onClick={() => {
                          setUnlinkRequested(!unlinkRequested);
                          setUnlinkComment('');
                          form.setFieldsValue({ discordUnlinkComment: '' });
                        }}
                      />
                    </Tooltip>
                  ) : null
                }
              />
            </FormItem>
          )}
          {unlinkRequested && (
            <FormItem
              label={content.ticketLink}
              name='discordUnlinkComment'
              preserve={false}
              rules={[{ required: true, whitespace: true, message: content.ticketRequired }]}
            >
              <Input
                placeholder={content.ticketLinkPlaceholder}
                disabled={isProcessing}
              />
            </FormItem>
          )}
          {isAdmin && (
            <>
              <FormItem
                label={content.email}
                name='email'
              >
                <Input
                  disabled={isDisableEmail || isSaving}
                  onChange={onPhoneOrEmailChangeHandler}
                  status={data?.authType === 'EMAIL' ? 'warning' : ''}
                />
              </FormItem>
              {data?.authType === 'PHONE' ? (
                <FormItem
                  label={content.phoneAuth}
                  name='phoneAuth'
                >
                  <Input />
                </FormItem>
              ) : null}
              <FormItem
                label={content.phoneAuth}
                name='phoneAuth'
              >
                <Input />
              </FormItem>
              <FormItem
                label={content.name}
                name='name'
              >
                <Input />
              </FormItem>
              <FormItem
                label={content.phone}
                name='phone'
              >
                <Input />
              </FormItem>
              <FormItem
                label={content.aboutMe}
                name='about'
              >
                <Input />
              </FormItem>
              {data?.id && (
                <FormItem
                  label={content.country}
                  name='countryCode'
                >
                  <Select
                    showSearch
                    disabled={!isAdmin || isSaving}
                    options={countryOptions}
                    optionFilterProp='label'
                  />
                </FormItem>
              )}
              <FormItem
                label={content.link}
                name='link'
              >
                <Input />
              </FormItem>
              <FormItem
                label={content.roleId}
                name='roleId'
              >
                <Select style={{ width: '100%' }}>
                  {list.map((role) => (
                    <Option
                      value={role.id}
                      key={role.id}
                    >
                      {content[role.name]}
                    </Option>
                  ))}
                </Select>
              </FormItem>
              <FormItem
                label={content.verify}
                name='isOfficial'
                valuePropName='checked'
              >
                <Checkbox disabled={!isAdmin || isSaving} />
              </FormItem>
              {!data?.id ? (
                <FormItem
                  label={content.password}
                  name='password'
                >
                  <Input.Password />
                </FormItem>
              ) : (
                <>
                  <Popconfirm
                    title={content.recoverPasswordConfirm}
                    onConfirm={recoverPasswordHandler}
                    okButtonProps={{ loading: Boolean(preloaders.recoverPassword) }}
                  >
                    <Button>{content.recoverPassword}</Button>
                  </Popconfirm>
                  <Popconfirm
                    visible={recoverMessageVisible}
                    title={content.recoverPasswordMessage}
                    showCancel={false}
                    onConfirm={() => {
                      setRecoverMessageVisible(false);
                    }}
                  ></Popconfirm>
                </>
              )}
            </>
          )}
        </Spin>
      </Modal>
    </Form>
  );
};

export default UserEditModal;
